if (!customElements.get('theme-product-card')) {
  let mediaHintClaimed = false;
  let mediaHintObserver;

  const observeMediaHint = card => {
    if (mediaHintClaimed || matchMedia('(prefers-reduced-motion: reduce)').matches || !('IntersectionObserver' in window)) return;
    mediaHintObserver ||= new IntersectionObserver(entries => {
      const visibleEntries = entries
        .filter(entry => entry.isIntersecting && entry.intersectionRatio >= .65)
        .sort((entryA, entryB) => {
          const rectA = entryA.boundingClientRect;
          const rectB = entryB.boundingClientRect;
          return rectA.top - rectB.top || rectA.left - rectB.left;
        });
      const firstVisibleCard = visibleEntries[0]?.target.closest('theme-product-card');
      if (!firstVisibleCard || mediaHintClaimed) return;
      mediaHintClaimed = true;
      mediaHintObserver.disconnect();
      firstVisibleCard.startMediaHint();
    }, { threshold: [.65] });
    mediaHintObserver.observe(card.viewport);
  };

  customElements.define('theme-product-card', class extends HTMLElement {
    connectedCallback() {
      this.abortController?.abort();
      this.abortController = new AbortController();
      this.viewport = this.querySelector('[data-product-card-media]');
      this.allSlides = [...this.querySelectorAll('[data-product-card-slide]')];
      this.mobileMediaQuery = matchMedia('(max-width: 749px)');
      this.refreshSlides();
      this.pages = [...this.querySelectorAll('[data-product-card-image]')];
      this.previous = this.querySelector('[data-product-card-previous]');
      this.next = this.querySelector('[data-product-card-next]');
      this.track = this.querySelector('[data-product-card-track]');
      this.optionButtons = [...this.querySelectorAll('[data-product-card-option]')];
      try {
        this.cardVariants = JSON.parse(this.querySelector('[data-product-card-variants]')?.textContent || '[]');
      } catch {
        this.cardVariants = [];
      }
      this.currentVariant = this.cardVariants.find(variant => String(variant.id) === this.dataset.productCardInitialVariantId);
      this.manualVariantSelection = false;
      this.variantImageToken = 0;
      this.optionButtons.forEach(button => button.addEventListener('click', () => this.selectVariantOption(button), { signal: this.abortController.signal }));
      this.videos = [...this.querySelectorAll('.product-card__video')];
      if (this.videos.length && 'IntersectionObserver' in window && this.viewport) {
        this.videoObserver = new IntersectionObserver(entries => {
          entries.forEach(({ target, intersectionRatio }) => {
            if (intersectionRatio >= .6 && !document.hidden) target.play().catch(() => {});
            else target.pause();
          });
        }, { root: this.viewport, threshold: [0, .6] });
        this.videos.forEach(video => this.videoObserver.observe(video));
      }
      this.abortController.signal.addEventListener('abort', () => this.videos?.forEach(video => video.pause()), { once: true });
      document.addEventListener('visibilitychange', () => {
        if (document.hidden) this.videos.forEach(video => video.pause());
        else this.videos.forEach(video => {
          const rect = video.getBoundingClientRect();
          const cardRect = this.viewport.getBoundingClientRect();
          const isVisible = rect.width > 0 && rect.right > 0 && rect.left < innerWidth && cardRect.bottom > 0 && cardRect.top < innerHeight;
          if (isVisible && video.closest('[data-product-card-slide]')?.offsetLeft === this.slides[this.currentIndex]?.offsetLeft) video.play().catch(() => {});
        });
      }, { signal: this.abortController.signal });
      this.currentIndex = 0;
      this.pointerStartX = 0;
      this.scrollStart = 0;
      this.didDrag = false;
      this.pointerId = null;
      if (!this.viewport || this.slides.length < 2) return;

      const { signal } = this.abortController;
      this.pages.forEach(page => page.addEventListener('click', () => {
        this.cancelMediaHint(true);
        this.goTo(Number(page.dataset.productCardImage));
      }, { signal }));
      this.previous?.addEventListener('click', () => {
        this.cancelMediaHint(true);
        const step = this.getItemsPerPage();
        const previousIndex = this.currentIndex - step;
        this.goTo(previousIndex < 0 ? this.getLastPageIndex() : previousIndex);
      }, { signal });
      this.next?.addEventListener('click', () => {
        this.cancelMediaHint(true);
        const nextIndex = this.currentIndex + this.getItemsPerPage();
        this.goTo(nextIndex > this.getLastPageIndex() ? 0 : nextIndex);
      }, { signal });
      this.viewport.addEventListener('scroll', () => this.requestStateUpdate(), { passive: true, signal });
      this.viewport.addEventListener('pointerdown', event => this.onPointerDown(event), { signal });
      this.viewport.addEventListener('pointermove', event => this.onPointerMove(event), { signal });
      this.viewport.addEventListener('pointerup', event => this.onPointerUp(event), { signal });
      this.viewport.addEventListener('pointercancel', event => this.onPointerUp(event), { signal });
      this.viewport.addEventListener('click', event => {
        if (!this.didDrag) return;
        event.preventDefault();
        event.stopPropagation();
        this.didDrag = false;
      }, { capture: true, signal });
      this.resizeObserver = new ResizeObserver(() => {
        this.refreshSlides();
        this.goTo(this.currentIndex, false);
      });
      this.resizeObserver.observe(this.viewport);
      if (this.isFirstCollectionCard()) observeMediaHint(this);
    }

    startMediaHint() {
      if (!this.track || this.hasMediaInteraction || this.currentIndex !== 0 || this.isMobileSliderDisabled() || matchMedia('(prefers-reduced-motion: reduce)').matches) return;
      this.classList.add('is-media-hinting');
      this.mediaHintAnimation = this.track.animate([
        { transform: 'translate3d(0, 0, 0)', offset: 0 },
        { transform: 'translate3d(0, 0, 0)', offset: .22 },
        { transform: 'translate3d(-8%, 0, 0)', offset: .56 },
        { transform: 'translate3d(-8%, 0, 0)', offset: .7 },
        { transform: 'translate3d(0, 0, 0)', offset: 1 }
      ], { duration: 1200, delay: 320, easing: 'cubic-bezier(.22, 1, .36, 1)' });
      this.mediaHintAnimation.finished.catch(() => {}).then(() => {
        this.classList.remove('is-media-hinting');
        this.mediaHintAnimation = null;
      });
    }

    cancelMediaHint(markInteraction = false) {
      if (markInteraction) this.hasMediaInteraction = true;
      this.mediaHintAnimation?.cancel();
      this.mediaHintAnimation = null;
      this.classList.remove('is-media-hinting');
    }

    refreshSlides() {
      this.slides = (this.allSlides || []).filter(slide => getComputedStyle(slide).display !== 'none');
    }

    getItemsPerPage() {
      return this.dataset.mobileSplit === 'true' && this.mobileMediaQuery?.matches ? 2 : 1;
    }

    isMobileSliderDisabled() {
      return this.classList.contains('product-card--mobile-slider-disabled') && this.mobileMediaQuery?.matches;
    }

    isFirstCollectionCard() {
      const collectionGrid = this.closest('.collection-main__grid');
      return collectionGrid?.querySelector(':scope > [data-product-card]') === this;
    }

    getLastPageIndex() {
      const step = this.getItemsPerPage();
      return Math.max(0, Math.floor((this.slides.length - 1) / step) * step);
    }

    getNearestPageIndex() {
      const step = this.getItemsPerPage();
      const scrollLeft = this.viewport?.scrollLeft || 0;
      let nearestIndex = 0;
      let nearestDistance = Number.POSITIVE_INFINITY;
      for (let index = 0; index < this.slides.length; index += step) {
        const distance = Math.abs(this.slides[index].offsetLeft - scrollLeft);
        if (distance >= nearestDistance) continue;
        nearestDistance = distance;
        nearestIndex = index;
      }
      return nearestIndex;
    }

    selectVariantOption(button) {
      if (!this.currentVariant || !this.cardVariants.length) return;
      const position = Number(button.dataset.optionPosition) - 1;
      const value = button.dataset.optionValue;
      if (position < 0 || !value) return;
      const options = [...this.currentVariant.options];
      options[position] = value;
      const visiblePositions = new Set(this.optionButtons.map(option => Number(option.dataset.optionPosition) - 1));
      const variant = this.cardVariants.find(item => item.options.every((option, index) => option === options[index]))
        || this.cardVariants.find(item => item.available && [...visiblePositions].every(index => item.options[index] === options[index]));
      if (!variant) return;
      this.cancelMediaHint(true);
      this.currentVariant = variant;
      this.manualVariantSelection = true;
      this.syncCardVariant(variant);
      this.updateCardVariantImage(variant);
      const primarySlide = this.querySelector('[data-product-card-primary-slide]');
      const primaryIndex = this.slides.indexOf(primarySlide);
      this.goTo(primaryIndex >= 0 ? primaryIndex : 0, false);
    }

    syncCardVariant(variant) {
      if (!variant) return;
      this.optionButtons.forEach(button => {
        const position = Number(button.dataset.optionPosition) - 1;
        button.setAttribute('aria-pressed', String(variant.options[position] === button.dataset.optionValue));
      });
      this.querySelectorAll('.product-card__cta .button__price').forEach(label => { label.textContent = variant.price; });
      this.querySelectorAll('[data-product-card-current-price]').forEach(label => { label.textContent = variant.price; });
      this.querySelectorAll('[data-product-card-compare-price]').forEach(label => {
        label.textContent = variant.compare || '';
        label.hidden = !variant.compare;
      });
      this.querySelectorAll('.product-card__cta .button__prices').forEach(prices => {
        let compare = prices.querySelector('.button__compare-price');
        if (!compare && variant.compare) {
          compare = document.createElement('s');
          compare.className = 'button__compare-price';
          prices.append(compare);
        }
        if (compare) {
          compare.textContent = variant.compare || '';
          compare.hidden = !variant.compare;
        }
      });
      this.querySelectorAll('.product-card__media-slide, .product-card__title a, .product-card__cta[href], .product-card__media-action[href]').forEach(link => {
        if (!link.dataset.productCardBaseHref) link.dataset.productCardBaseHref = link.getAttribute('href') || '';
        const url = new URL(link.dataset.productCardBaseHref, location.origin);
        url.searchParams.set('variant', variant.id);
        link.href = `${url.pathname}${url.search}${url.hash}`;
      });
    }

    updateCardVariantImage(variant) {
      const image = this.querySelector('[data-product-card-variant-image]');
      if (!image) return;
      const token = ++this.variantImageToken;
      image.classList.remove('is-visible');
      if (!variant.image) {
        image.hidden = true;
        image.removeAttribute('src');
        return;
      }
      image.hidden = false;
      image.src = variant.image;
      image.decode().catch(() => {}).then(() => {
        if (token !== this.variantImageToken || !this.isConnected) return;
        requestAnimationFrame(() => image.classList.add('is-visible'));
      });
    }

    goTo(index, animate = true) {
      this.refreshSlides();
      if (!this.slides.length) return;
      const step = this.getItemsPerPage();
      const requestedIndex = Math.floor(Math.max(0, index) / step) * step;
      const nextIndex = Math.min(requestedIndex, this.getLastPageIndex());
      const reducedMotion = matchMedia('(prefers-reduced-motion: reduce)').matches;
      this.viewport?.scrollTo({ left: this.slides[nextIndex].offsetLeft, behavior: animate && !reducedMotion ? 'smooth' : 'auto' });
      this.updateState(nextIndex);
    }

    requestStateUpdate() {
      if (this.frame) return;
      this.frame = requestAnimationFrame(() => {
        this.frame = null;
        this.updateState(this.getNearestPageIndex());
      });
    }

    updateState(index) {
      this.currentIndex = Math.max(0, Math.min(index, this.slides.length - 1));
      this.pages.forEach(page => page.setAttribute('aria-current', Number(page.dataset.productCardImage) === this.currentIndex ? 'true' : 'false'));
      const slideVariant = this.cardVariants?.find(variant => String(variant.id) === this.slides[this.currentIndex]?.dataset.productCardSlideVariantId);
      if (this.manualVariantSelection || slideVariant) {
        if (!this.manualVariantSelection) this.currentVariant = slideVariant;
        this.syncCardVariant(this.currentVariant);
        return;
      }
      const price = this.slides[this.currentIndex]?.dataset.productCardSlidePrice;
      if (price) this.querySelectorAll('.product-card__cta .button__price').forEach(label => { label.textContent = price; });
      const comparePrice = this.slides[this.currentIndex]?.dataset.productCardSlideComparePrice || '';
      if (price) this.querySelectorAll('[data-product-card-current-price]').forEach(label => { label.textContent = price; });
      this.querySelectorAll('[data-product-card-compare-price]').forEach(label => {
        label.textContent = comparePrice;
        label.hidden = !comparePrice;
      });
      this.querySelectorAll('.product-card__cta .button__prices').forEach(prices => {
        let compare = prices.querySelector('.button__compare-price');
        if (!compare && comparePrice) {
          compare = document.createElement('s');
          compare.className = 'button__compare-price';
          prices.append(compare);
        }
        if (compare) {
          compare.textContent = comparePrice;
          compare.hidden = !comparePrice;
        }
      });
    }

    onPointerDown(event) {
      if (this.isMobileSliderDisabled()) return;
      this.cancelMediaHint(true);
      if (event.pointerType !== 'mouse' || event.button !== 0) return;
      this.pointerStartX = event.clientX;
      this.scrollStart = this.viewport.scrollLeft;
      this.didDrag = false;
      this.pointerId = event.pointerId;
    }

    onPointerMove(event) {
      if (this.pointerId !== event.pointerId) return;
      const distance = event.clientX - this.pointerStartX;
      if (Math.abs(distance) > 12 && !this.didDrag) {
        this.didDrag = true;
        this.viewport.setPointerCapture(event.pointerId);
        this.viewport.classList.add('is-dragging');
      }
      if (this.didDrag) this.viewport.scrollLeft = this.scrollStart - distance;
    }

    onPointerUp(event) {
      if (this.pointerId !== event.pointerId) return;
      if (this.viewport.hasPointerCapture(event.pointerId)) this.viewport.releasePointerCapture(event.pointerId);
      this.pointerId = null;
      this.viewport.classList.remove('is-dragging');
      if (this.didDrag) {
        this.goTo(this.getNearestPageIndex());
      }
    }

    disconnectedCallback() {
      this.cancelMediaHint();
      this.variantImageToken++;
      mediaHintObserver?.unobserve(this.viewport);
      this.abortController?.abort();
      this.resizeObserver?.disconnect();
      this.videoObserver?.disconnect();
      this.videos?.forEach(video => video.pause());
      if (this.frame) cancelAnimationFrame(this.frame);
    }
  });
}
